/**
 * Google 廣告追蹤載入器（2026-08-28）
 *
 * 後台「內容編輯 → Google 廣告追蹤」設定，存 siteConfig.googleTracking：
 * - gtmId：GTM-XXXXXXX（一個容器管理全部代碼；填咗 GTM 就唔會載入其他）
 * - adsId：AW-XXXXXXX（無 GTM 時直接載 gtag.js 追蹤廣告）
 * - ga4Id：G-XXXXXXX（可選，流量分析）
 * - whatsappLabel：AW-XXXXXXX/XXXXXX（可選——點擊全站任何 WhatsApp 連結＝廣告轉化）
 *
 * 安全：ID 需通過格式驗證才注入（防經 CMS 欄位注入腳本）。
 */

export interface GoogleTrackingConfig {
  gtmId?: string;
  adsId?: string;
  ga4Id?: string;
  whatsappLabel?: string;
}

const RE_GTM = /^GTM-[A-Z0-9]{4,12}$/i;
const RE_ADS = /^AW-[0-9]{6,12}$/i;
const RE_GA4 = /^G-[A-Z0-9]{6,12}$/i;
const RE_LABEL = /^AW-[0-9]{6,12}\/[A-Za-z0-9_-]{4,32}$/i;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __marvelandTrackingInit?: boolean;
  }
}

export function initGoogleTracking(cfg?: GoogleTrackingConfig): void {
  // 靜態 base tag 已存在（index.html 內建 AW-18405949523＋WhatsApp 轉化委派）→
  // 只處理 CMS 的 GA4 額外設定，不再重複注入與註冊（避免重複計數）
  if (document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
    const ga4Id = (cfg?.ga4Id || '').trim();
    if (ga4Id && RE_GA4.test(ga4Id) && window.dataLayer) {
      window.dataLayer.push(['config', ga4Id]);
    }
    return;
  }

  if (!cfg || window.__marvelandTrackingInit) return;

  const gtmId = (cfg.gtmId || '').trim();
  const adsId = (cfg.adsId || '').trim();
  const ga4Id = (cfg.ga4Id || '').trim();
  const whatsappLabel = (cfg.whatsappLabel || '').trim();

  if (gtmId && RE_GTM.test(gtmId)) {
    // GTM 標準片段（無 noscript 後備——SPA 場景捲動才可見，影響極小）
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
    document.head.appendChild(s);
  } else if ((adsId && RE_ADS.test(adsId)) || (ga4Id && RE_GA4.test(ga4Id))) {
    const primary = adsId && RE_ADS.test(adsId) ? adsId : ga4Id;
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primary)}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
    window.gtag('js', new Date());
    if (adsId && RE_ADS.test(adsId)) window.gtag('config', adsId);
    if (ga4Id && RE_GA4.test(ga4Id)) window.gtag('config', ga4Id);
  } else {
    return; // 什麼都沒配置——不載入任何追蹤
  }

  window.__marvelandTrackingInit = true;
  // WhatsApp 轉化監聽已由 index.html 靜態委派處理（支援同事專屬 data-wa-label
  // 與公司 Label），此處不再重複註冊
}
